export const ENDPOINTS = {
  auth: {
    login: '/merchant/auth/login',
    register: '/merchant/auth/register',
    logout: '/merchant/auth/logout',
    me: '/merchant/auth/me',
    refresh: '/merchant/auth/refresh',
    password: '/merchant/auth/password',
    switchStore: '/merchant/auth/switch-store',
  },
  users: {
    base: '/users',
    one: (id: string | number) => `/users/${id}`,
  },
  roles: {
    base: '/roles',
    one: (id: string | number) => `/roles/${id}`,
    permissions: '/roles/permissions',
  },


  productCategories: {
    base: '/merchant/product-categories',
    one: (id: string) => `/merchant/product-categories/${id}`,
    move: (id: string) => `/merchant/product-categories/${id}/move`,
    options: '/merchant/product-categories/options',
  },

  products: {
    base: '/merchant/products',
    one: (id: string) => `/merchant/products/${id}`,
    status: (id: string) => `/merchant/products/${id}/status`,
    duplicate: (id: string) => `/merchant/products/${id}/duplicate`,
    restore: (id: string) => `/merchant/products/${id}/restore`,
    bulk: '/merchant/products/bulk',
    import: '/merchant/products/import',
    export: '/merchant/products/export',
    options: '/merchant/products/options',
    regenerateVariants: (id: string) => `/merchant/products/${id}/regenerate-variants`,
    variants: {
      base: (productId: string) => `/merchant/products/${productId}/variants`,
      one: (productId: string, variantId: string) => `/merchant/products/${productId}/variants/${variantId}`,
      setDefault: (productId: string, variantId: string) => `/merchant/products/${productId}/variants/${variantId}/default`,
      toggleActive: (productId: string, variantId: string) => `/merchant/products/${productId}/variants/${variantId}/toggle-active`,
      image: (productId: string, variantId: string) => `/merchant/products/${productId}/variants/${variantId}/image`,
    },
  },

  productBrands: {
    base: '/merchant/product-brands',
    one: (id: string) => `/merchant/product-brands/${id}`,
    activate: (id: string) => `/merchant/product-brands/${id}/activate`,
    deactivate: (id: string) => `/merchant/product-brands/${id}/deactivate`,
    options: '/merchant/product-brands/options',
  },

  merchantSummary: '/merchant/summary',

  merchantDashboard: {
    base: '/merchant/dashboard',
    storefrontOpen: '/merchant/storefront/open',
    launch: '/merchant/store/launch',
  },

  billing: {
    // plans: '/merchant/onboarding/subscription-plans',
    plans: '/merchant/billing/plans',
    subscription: '/merchant/billing/subscription',
    subscribeTrial: '/merchant/billing/subscriptions/trial',
    // ⚠️ Trial mode (no payment gateway): activates subscription immediately as Active.
    // TODO: when gateway is live, replace with a real checkout endpoint.
    checkoutTrial: '/merchant/billing/subscriptions/checkout/trial',
    autoRenew: '/merchant/billing/subscription/auto-renew',
  },

  aiCredits: {
    balance: '/merchant/ai-credits',
    packs: '/merchant/ai-credits/packs',
    purchases: '/merchant/ai-credits/purchases',
  },

  aiChat: {
    sessions: '/merchant/ai-chat/sessions',
    proposal: (proposalId: string) => `/merchant/ai-chat/actions/${proposalId}`,
    confirmProposal: (proposalId: string) => `/merchant/ai-chat/actions/${proposalId}/confirm`,
    rejectProposal: (proposalId: string) => `/merchant/ai-chat/actions/${proposalId}/reject`,
    messages: (sessionId: string) => `/merchant/ai-chat/sessions/${sessionId}/messages`,
    action: (sessionId: string, messageId: string) => `/merchant/ai-chat/sessions/${sessionId}/messages/${messageId}/actions`,
    stream: (sessionId: string) => `/merchant/ai-chat/sessions/${sessionId}/messages/stream`,
  },

  onboarding: {
    industries: '/merchant/onboarding/industries',
    locations: '/merchant/onboarding/locations',
    startupQuestions: '/merchant/onboarding/startup-questions',
    themes: '/merchant/onboarding/themes',
    subscriptionPlans: '/merchant/onboarding/subscription-plans',
    state: '/merchant/onboarding/state',
    subdomainCheck: '/merchant/onboarding/subdomain-availability',
    complete: '/merchant/onboarding/complete',
    steps: {
      storeBasics: '/merchant/onboarding/steps/store-basics',
      location: '/merchant/onboarding/steps/location',
      industry: '/merchant/onboarding/steps/industry',
      startupQuestions: '/merchant/onboarding/steps/startup-questions',
      subscriptionPlans: '/merchant/onboarding/steps/subscription-plans',
      theme: '/merchant/onboarding/steps/theme',
    },
  },

  aiOnboarding: {
    chat: '/merchant/ai-onboarding/chat',
    state: '/merchant/ai-onboarding/state',
    resume: '/merchant/ai-onboarding/resume',
    transcribe: '/merchant/ai-onboarding/transcribe',
    translate: '/merchant/ai-onboarding/translate',
    suggestName: '/merchant/ai-onboarding/suggest-name',
    suggestSubdomain: '/merchant/ai-onboarding/suggest-subdomain',
  },
  customers: {
    base: '/merchant/customers',
    one: (id: string) => `/merchant/customers/${id}`,
    block: (id: string) => `/merchant/customers/${id}/block`,
    unblock: (id: string) => `/merchant/customers/${id}/unblock`,
    export: '/merchant/customers/export',
    options: '/merchant/customers/options',
    classificationOptions: '/merchant/customers/classification-options',
  },

  shipping: {
    zones: {
      base: '/merchant/shipping/zones',
      one: (id: string) => `/merchant/shipping/zones/${id}`,
      seedSaudiDefaults: '/merchant/shipping/seed-saudi-defaults',
    },
    methods: {
      base: (zoneId: string) => `/merchant/shipping/zones/${zoneId}/methods`,
      one: (zoneId: string, methodId: string) => `/merchant/shipping/zones/${zoneId}/methods/${methodId}`,
      reorder: (zoneId: string) => `/merchant/shipping/zones/${zoneId}/methods/reorder`,
    },
  },

  discounts: {
    base: '/merchant/discounts',
    one: (id: string) => `/merchant/discounts/${id}`,
    toggle: (id: string) => `/merchant/discounts/${id}/toggle`,
    duplicate: (id: string) => `/merchant/discounts/${id}/duplicate`,
    generateCode: '/merchant/discounts/generate-code',
    redemptions: (id: string) => `/merchant/discounts/${id}/redemptions`,
  },

  countries: {
    base: '/merchant/onboarding/locations',
    one: (id: string) => `/merchant/countries/${id}`,
    status: (id: string) => `/merchant/countries/${id}/status`,
    states: (countryId: string) => `/merchant/countries/${countryId}/states`,
    state: (countryId: string, stateId: string) => `/merchant/countries/${countryId}/states/${stateId}`,
    stateStatus: (countryId: string, stateId: string) => `/merchant/countries/${countryId}/states/${stateId}/status`,
  },

  checkout: {
    configuration: '/merchant/checkout/configuration',
  },

  legalPages: {
    base: '/merchant/legal-pages',
    one: (id: string) => `/merchant/legal-pages/${id}`,
  },

  smartCollections: {
    base: '/merchant/smart-collections',
    one: (id: string) => `/merchant/smart-collections/${id}`,
  },

  orders: {
    base: '/merchant/orders',
    stats: '/merchant/orders/stats',
    export: '/merchant/orders/export',
    one: (id: string) => `/merchant/orders/${id}`,
    packingSlip: (id: string) => `/merchant/orders/${id}/packing-slip`,
    note: (id: string) => `/merchant/orders/${id}/note`,
    prepare: (id: string) => `/merchant/orders/${id}/prepare`,
    ship: (id: string) => `/merchant/orders/${id}/ship`,
    deliver: (id: string) => `/merchant/orders/${id}/deliver`,
    markPaid: (id: string) => `/merchant/orders/${id}/mark-paid`,
    cancel: (id: string) => `/merchant/orders/${id}/cancel`,
    refund: (id: string) => `/merchant/orders/${id}/refund`,
  },

  themeWorkspace: {
    base: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace`,
    draft: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/draft`,
    apply: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/apply`,
    reset: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/reset`,
    publish: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/publish`,
    previewToken: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/preview-token`,
    style: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/style`,
    branding: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/branding`,
    sectionContent: (storeId: string, sectionId: string) => `/merchant/stores/${storeId}/theme-workspace/sections/${sectionId}/content`,
    sectionSettings: (storeId: string, sectionId: string) => `/merchant/stores/${storeId}/theme-workspace/sections/${sectionId}/settings`,
    sectionVisibility: (storeId: string, sectionId: string) => `/merchant/stores/${storeId}/theme-workspace/sections/${sectionId}/visibility`,
    sectionVariant: (storeId: string, sectionId: string) => `/merchant/stores/${storeId}/theme-workspace/sections/${sectionId}/variant`,
    sections: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/sections`,
    section: (storeId: string, sectionId: string) => `/merchant/stores/${storeId}/theme-workspace/sections/${sectionId}`,
    reorderSections: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/sections/reorder`,
    message: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/message`,
    proposal: (storeId: string, proposalId: string) => `/merchant/stores/${storeId}/theme-workspace/actions/${proposalId}`,
    confirmProposal: (storeId: string, proposalId: string) => `/merchant/stores/${storeId}/theme-workspace/actions/${proposalId}/confirm`,
    rejectProposal: (storeId: string, proposalId: string) => `/merchant/stores/${storeId}/theme-workspace/actions/${proposalId}/reject`,
    history: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/history`,
    undo: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/history/undo`,
    redo: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/history/redo`,
    revertTo: (storeId: string, snapshotId: string) => `/merchant/stores/${storeId}/theme-workspace/history/${snapshotId}/revert`,
    voiceTranscribe: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/voice-transcribe`,
    clearMessages: (storeId: string) => `/merchant/stores/${storeId}/theme-workspace/messages`,
  },

  themes: {
    schema: (themeId: string) => `/merchant/themes/${themeId}/schema`,
  },

} as const
